import { useState, useEffect } from "react";
import {
  Sunrise,
  Clock,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
  Bell,
} from "lucide-react";
import { DayPlan, DayPlanItem, BadgeDefinition } from "../lib/supabase";
import {
  wakeUp,
  getTodayPlan,
  markDoseTaken,
  markDoseMissed,
  snoozeDose,
  clearTodayPlan,
} from "../lib/api";
import { formatTime, formatFoodRelation } from "../lib/scheduler";
import {
  scheduleNotificationsForPlan,
  isSubscribedToPushNotifications,
} from "../lib/notifications";
import {
  incrementDoseTaken,
  checkAndUpdateStreak,
  getAllBadges,
  getUserBadges,
  getUserStats,
} from "../lib/gamification";
import { NotificationBanner } from "./NotificationBanner";
import { Toast } from "./Toast";
import { ParticleBurst } from "./ParticleBurst";
import { StreakDisplay } from "./StreakDisplay";
import { FunFact } from "./FunFact";
import { BadgeUnlockModal } from "./BadgeUnlockModal";
import { PlanGenerationModal } from "./PlanGenerationModal";
import { GroupCard } from "./GroupCard";

interface ToastState {
  message: string;
  onUndo?: () => void;
}

interface GroupedItem {
  type: "single" | "group";
  groupInstanceId?: string;
  items: DayPlanItem[];
}

interface CompletionState {
  itemId: string;
  showParticles: boolean;
}

export function TodayDashboard() {
  const [plan, setPlan] = useState<(DayPlan & { items: DayPlanItem[] }) | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [waking, setWaking] = useState(false);
  const [showGenerationModal, setShowGenerationModal] = useState(false);
  const [isRecompute, setIsRecompute] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<ToastState | null>(null);
  const [completionStates, setCompletionStates] = useState<CompletionState[]>(
    []
  );
  const [groupCompletionStates, setGroupCompletionStates] = useState<
    Set<string>
  >(new Set());
  const [unlockedBadge, setUnlockedBadge] = useState<BadgeDefinition | null>(
    null
  );

  useEffect(() => {
    loadPlan();

    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener(
        "message",
        handleServiceWorkerMessage
      );
    }

    return () => {
      if ("serviceWorker" in navigator) {
        navigator.serviceWorker.removeEventListener(
          "message",
          handleServiceWorkerMessage
        );
      }
    };
  }, []);

  const handleServiceWorkerMessage = async (event: MessageEvent) => {
    if (event.data.type === "NOTIFICATION_ACTION") {
      const { action, itemId } = event.data;

      switch (action) {
        case "taken":
          await handleTaken(itemId);
          break;
        case "snooze_10":
          await handleSnooze(itemId, 10);
          break;
        case "snooze_30":
          await handleSnooze(itemId, 30);
          break;
        case "missed":
          await handleMissed(itemId);
          break;
        default:
          await loadPlan();
      }
    }
  };

  const loadPlan = async () => {
    setLoading(true);
    try {
      const todayPlan = await getTodayPlan();
      setPlan(todayPlan);
    } finally {
      setLoading(false);
    }
  };

  const handleWakeUp = async () => {
    setWaking(true);
    setIsRecompute(!!plan);
    setShowGenerationModal(true);

    try {
      const newPlan = await wakeUp();

      await new Promise((resolve) => setTimeout(resolve, 1500));

      setPlan(newPlan);

      const isSubscribed = await isSubscribedToPushNotifications();
      if (isSubscribed) {
        const notificationsScheduled = await scheduleNotificationsForPlan(
          newPlan.id
        );
        if (notificationsScheduled) {
          console.log("Notifications scheduled for plan");
        }
      }

      setShowGenerationModal(false);

      await new Promise((resolve) => setTimeout(resolve, 300));

      setToast({
        message: plan
          ? "Plan recomputed successfully"
          : "Plan created successfully",
      });
    } catch (error: any) {
      setShowGenerationModal(false);
      setToast({
        message: error.message || "Error generating plan",
      });
    } finally {
      setWaking(false);
    }
  };

  const handleTaken = async (itemId: string) => {
    if (!plan) return;

    const originalItem = plan.items.find((item) => item.id === itemId);
    if (!originalItem) return;

    setCompletionStates([...completionStates, { itemId, showParticles: true }]);

    setTimeout(() => {
      setCompletionStates((prev) => prev.filter((s) => s.itemId !== itemId));
    }, 600);

    const updatedItem = await markDoseTaken(itemId);
    await incrementDoseTaken();

    setPlan({
      ...plan,
      items: plan.items.map((item) =>
        item.id === itemId ? updatedItem : item
      ),
    });

    await checkAndUpdateStreak();
    await checkForNewBadges();

    const allTaken = plan.items.every(
      (item) => item.id === itemId || item.status === "TAKEN"
    );

    if (allTaken) {
      setToast({
        message: "🎉 All doses completed for today! Amazing work!",
      });
    } else {
      setToast({
        message: `${originalItem.preset_snapshot.name} marked as taken`,
        onUndo: async () => {
          setPlan({
            ...plan,
            items: plan.items.map((item) =>
              item.id === itemId ? originalItem : item
            ),
          });
          await snoozeDose(itemId, 0);
          setToast(null);
        },
      });
    }
  };

  const handleMissed = async (itemId: string) => {
    if (!plan) return;

    const originalItem = plan.items.find((item) => item.id === itemId);
    if (!originalItem) return;

    const updatedItem = await markDoseMissed(itemId);

    setPlan({
      ...plan,
      items: plan.items.map((item) =>
        item.id === itemId ? updatedItem : item
      ),
    });

    setToast({
      message: `${originalItem.preset_snapshot.name} marked as missed`,
      onUndo: async () => {
        setPlan({
          ...plan,
          items: plan.items.map((item) =>
            item.id === itemId ? originalItem : item
          ),
        });
        await snoozeDose(itemId, 0);
        setToast(null);
      },
    });
  };

  const handleSnooze = async (itemId: string, minutes: number) => {
    if (!plan) return;

    const originalItem = plan.items.find((item) => item.id === itemId);
    if (!originalItem) return;

    const updatedItem = await snoozeDose(itemId, minutes);

    setPlan({
      ...plan,
      items: plan.items.map((item) =>
        item.id === itemId ? updatedItem : item
      ),
    });

    setToast({
      message: `${originalItem.preset_snapshot.name} snoozed ${minutes} minutes`,
      onUndo: async () => {
        setPlan({
          ...plan,
          items: plan.items.map((item) =>
            item.id === itemId ? originalItem : item
          ),
        });
        await snoozeDose(itemId, -minutes);
        setToast(null);
      },
    });
  };

  const handleGroupTaken = async (itemIds: string[]) => {
    if (!plan) return;

    const firstItem = plan.items.find((item) => item.id === itemIds[0]);
    if (!firstItem || !firstItem.group_instance_id) return;

    setGroupCompletionStates((prev) =>
      new Set(prev).add(firstItem.group_instance_id!)
    );

    setTimeout(() => {
      setGroupCompletionStates((prev) => {
        const next = new Set(prev);
        next.delete(firstItem.group_instance_id!);
        return next;
      });
    }, 600);

    const originalItems = plan.items.filter((item) =>
      itemIds.includes(item.id)
    );

    const updatedItems = await Promise.all(
      itemIds.map((id) => markDoseTaken(id))
    );

    await incrementDoseTaken(itemIds.length);

    setPlan({
      ...plan,
      items: plan.items.map((item) => {
        const updated = updatedItems.find((u) => u.id === item.id);
        return updated || item;
      }),
    });

    await checkAndUpdateStreak();
    await checkForNewBadges();

    const allTaken = plan.items.every(
      (item) => itemIds.includes(item.id) || item.status === "TAKEN"
    );

    if (allTaken) {
      setToast({
        message: "🎉 All doses completed for today! Amazing work!",
      });
    } else {
      setToast({
        message: `${
          firstItem.source_group_snapshot?.name || "Group"
        } marked as taken`,
        onUndo: async () => {
          setPlan({
            ...plan,
            items: plan.items.map((item) => {
              const original = originalItems.find((o) => o.id === item.id);
              return original || item;
            }),
          });
          await Promise.all(itemIds.map((id) => snoozeDose(id, 0)));
          setToast(null);
        },
      });
    }
  };

  const handleGroupMissed = async (itemIds: string[]) => {
    if (!plan) return;

    const firstItem = plan.items.find((item) => item.id === itemIds[0]);
    if (!firstItem) return;

    const originalItems = plan.items.filter((item) =>
      itemIds.includes(item.id)
    );

    const updatedItems = await Promise.all(
      itemIds.map((id) => markDoseMissed(id))
    );

    setPlan({
      ...plan,
      items: plan.items.map((item) => {
        const updated = updatedItems.find((u) => u.id === item.id);
        return updated || item;
      }),
    });

    setToast({
      message: `${
        firstItem.source_group_snapshot?.name || "Group"
      } marked as missed`,
      onUndo: async () => {
        setPlan({
          ...plan,
          items: plan.items.map((item) => {
            const original = originalItems.find((o) => o.id === item.id);
            return original || item;
          }),
        });
        await Promise.all(itemIds.map((id) => snoozeDose(id, 0)));
        setToast(null);
      },
    });
  };

  const handleGroupSnooze = async (itemIds: string[], minutes: number) => {
    if (!plan) return;

    const firstItem = plan.items.find((item) => item.id === itemIds[0]);
    if (!firstItem) return;

    const originalItems = plan.items.filter((item) =>
      itemIds.includes(item.id)
    );

    const updatedItems = await Promise.all(
      itemIds.map((id) => snoozeDose(id, minutes))
    );

    setPlan({
      ...plan,
      items: plan.items.map((item) => {
        const updated = updatedItems.find((u) => u.id === item.id);
        return updated || item;
      }),
    });

    setToast({
      message: `${
        firstItem.source_group_snapshot?.name || "Group"
      } snoozed ${minutes} minutes`,
      onUndo: async () => {
        setPlan({
          ...plan,
          items: plan.items.map((item) => {
            const original = originalItems.find((o) => o.id === item.id);
            return original || item;
          }),
        });
        await Promise.all(itemIds.map((id) => snoozeDose(id, -minutes)));
        setToast(null);
      },
    });
  };

  const checkForNewBadges = async () => {
    const [allBadges, userBadges] = await Promise.all([
      getAllBadges(),
      getUserBadges(),
    ]);

    const earnedIds = new Set(userBadges.map((ub) => ub.badge_id));

    for (const badge of allBadges) {
      if (!earnedIds.has(badge.id) && (await shouldShowBadge(badge.id))) {
        setUnlockedBadge(badge);
        break; // Show only one badge at a time
      }
    }
  };

  const shouldShowBadge = async (badgeId: string): Promise<boolean> => {
    const stats = await getUserStats();
    if (!stats) return false;

    switch (badgeId) {
      case "perfect_week":
        return stats.current_streak_days >= 7;
      case "consistency_champion":
        return stats.current_streak_days >= 30;
      case "iron_will":
        return stats.current_streak_days >= 90;
      case "health_warrior":
        return stats.current_streak_days >= 365;
      case "century_club":
        return stats.total_doses_taken >= 100;
      case "knowledge_seeker":
        return stats.facts_viewed_count >= 50;
      default:
        return false;
    }
  };

  const groupItems = (items: DayPlanItem[]): GroupedItem[] => {
    const grouped = new Map<string, DayPlanItem[]>();
    const singles: DayPlanItem[] = [];

    items.forEach((item) => {
      if (item.group_instance_id) {
        const existing = grouped.get(item.group_instance_id);
        if (existing) {
          existing.push(item);
        } else {
          grouped.set(item.group_instance_id, [item]);
        }
      } else {
        singles.push(item);
      }
    });

    const result: GroupedItem[] = [];

    grouped.forEach((groupItems, groupInstanceId) => {
      result.push({
        type: "group",
        groupInstanceId,
        items: groupItems,
      });
    });

    singles.forEach((item) => {
      result.push({
        type: "single",
        items: [item],
      });
    });

    result.sort((a, b) => {
      const aTime = new Date(a.items[0].planned_time).getTime();
      const bTime = new Date(b.items[0].planned_time).getTime();
      return aTime - bTime;
    });

    return result;
  };

  const handleClearPlan = async () => {
    if (!plan) return;

    const originalPlan = plan;

    try {
      await clearTodayPlan();
      setPlan(null);

      setToast({
        message: "Plan cleared successfully",
        onUndo: async () => {
          setPlan(originalPlan);
          await wakeUp();
          setToast(null);
        },
      });
    } catch (error: any) {
      setToast({
        message: error.message || "Error clearing plan",
      });
    }
  };

  const toggleExpanded = (itemId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "TAKEN":
        return "bg-green-50 border-green-200 text-green-800";
      case "MISSED":
        return "bg-red-50 border-red-200 text-red-800";
      case "SNOOZED":
        return "bg-yellow-50 border-yellow-200 text-yellow-800";
      default:
        return "bg-white border-gray-200 text-gray-900";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "TAKEN":
        return <CheckCircle className="text-green-600" size={20} />;
      case "MISSED":
        return <XCircle className="text-red-600" size={20} />;
      case "SNOOZED":
        return <Bell className="text-yellow-600" size={20} />;
      default:
        return <Clock className="text-gray-400" size={20} />;
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading...</div>;
  }

  if (!plan) {
    return (
      <div className="mobile-full-height bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 relative overflow-hidden safe-area-top">
        {/* Animated Background Layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-300/20 via-amber-300/15 to-yellow-300/25 animate-breathing"></div>

        {/* Wavy Animated Blobs */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          <div className="absolute top-[-20%] left-[-10%] w-[120%] h-[40%] bg-gradient-to-r from-orange-400/30 via-amber-400/20 to-yellow-400/25 rounded-full animate-wave-1 blur-3xl"></div>
          <div className="absolute top-[30%] right-[-15%] w-[100%] h-[50%] bg-gradient-to-l from-yellow-400/25 via-orange-300/30 to-amber-400/20 rounded-full animate-wave-2 blur-2xl"></div>
          <div className="absolute bottom-[-10%] left-[-20%] w-[140%] h-[35%] bg-gradient-to-tr from-amber-400/20 via-yellow-300/25 to-orange-400/15 rounded-full animate-wave-3 blur-xl"></div>
        </div>

        {/* Floating Elements */}
        <div className="absolute top-20 right-10 w-4 h-4 bg-yellow-400/40 rounded-full animate-float-fast blur-sm"></div>
        <div
          className="absolute top-40 left-8 w-6 h-6 bg-orange-400/30 rounded-full animate-float-slow blur-sm"
          style={{ animationDelay: "1s" }}
        ></div>
        <div
          className="absolute bottom-32 right-16 w-3 h-3 bg-amber-400/50 rounded-full animate-float-fast blur-sm"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="absolute top-60 right-20 w-5 h-5 bg-yellow-300/35 rounded-full animate-float-slow blur-sm"
          style={{ animationDelay: "3s" }}
        ></div>
        <div
          className="absolute top-80 left-16 w-4 h-4 bg-orange-300/40 rounded-full animate-gentle-sway blur-sm"
          style={{ animationDelay: "1.5s" }}
        ></div>
        <div
          className="absolute bottom-48 left-12 w-7 h-7 bg-yellow-400/25 rounded-full animate-float-fast blur-sm"
          style={{ animationDelay: "4s" }}
        ></div>
        <div
          className="absolute top-32 left-1/3 w-3 h-3 bg-amber-400/60 rounded-full animate-float-slow blur-sm"
          style={{ animationDelay: "2.5s" }}
        ></div>
        <div
          className="absolute bottom-20 right-1/4 w-5 h-5 bg-orange-400/35 rounded-full animate-gentle-sway blur-sm"
          style={{ animationDelay: "3.5s" }}
        ></div>

        <div className="relative min-h-[calc(100vh-200px)] flex items-center justify-center p-4 safe-area-x">
          <div className="flex flex-col items-center gap-6 max-w-sm w-full">
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">
                Good Morning!
              </h1>
              <p className="text-gray-700 text-base md:text-lg">
                Start your day with your supplement schedule
              </p>
            </div>

            <div className="relative group">
              <button
                onClick={handleWakeUp}
                disabled={waking}
                className="relative w-40 h-40 md:w-48 md:h-48 rounded-full bg-gradient-to-br from-orange-400 via-orange-500 to-yellow-500 text-white font-bold text-lg md:text-xl shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-110 hover:shadow-[0_0_80px_rgba(251,146,60,0.8)] active:scale-95 flex items-center justify-center overflow-hidden animate-breathing"
              >
                <div className="absolute inset-0 bg-gradient-to-t from-yellow-400 to-transparent opacity-0 group-hover:opacity-50 transition-opacity duration-500"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-transparent to-orange-600 opacity-0 group-hover:opacity-30 transition-opacity duration-700"></div>

                <div className="flex flex-col items-center gap-3 transition-transform duration-500 group-hover:-translate-y-2 animate-float">
                  <Sunrise
                    className="text-white drop-shadow-lg transition-all duration-500 group-hover:animate-sun-glow"
                    size={waking ? 48 : 56}
                    strokeWidth={2}
                  />
                  <span className="transition-all duration-300">
                    {waking ? "Waking..." : "Wake Up"}
                  </span>
                </div>
              </button>

              <div className="absolute inset-0 rounded-full bg-orange-400 opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-500 -z-10 scale-110 animate-breathing"></div>
              <div
                className="absolute inset-0 rounded-full bg-yellow-400 opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-700 -z-10 scale-125 animate-breathing"
                style={{ animationDelay: "1s" }}
              ></div>
              <div
                className="absolute inset-0 rounded-full bg-amber-400 opacity-0 group-hover:opacity-15 blur-3xl transition-opacity duration-1000 -z-10 scale-150 animate-breathing"
                style={{ animationDelay: "0.5s" }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const wakeTime = new Date(plan.wake_time);
  const now = new Date();

  return (
    <div className="mobile-full-height bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-100 relative overflow-hidden safe-area-top">
      {/* Animated Background Layers */}
      <div className="absolute inset-0 bg-gradient-to-br from-orange-300/20 via-amber-300/15 to-yellow-300/25 animate-breathing"></div>

      {/* Wavy Animated Blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[120%] h-[40%] bg-gradient-to-r from-orange-400/30 via-amber-400/20 to-yellow-400/25 rounded-full animate-wave-1 blur-3xl"></div>
        <div className="absolute top-[30%] right-[-15%] w-[100%] h-[50%] bg-gradient-to-l from-yellow-400/25 via-orange-300/30 to-amber-400/20 rounded-full animate-wave-2 blur-2xl"></div>
        <div className="absolute bottom-[-10%] left-[-20%] w-[140%] h-[35%] bg-gradient-to-tr from-amber-400/20 via-yellow-300/25 to-orange-400/15 rounded-full animate-wave-3 blur-xl"></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-20 right-10 w-4 h-4 bg-yellow-400/40 rounded-full animate-float blur-sm"></div>
      <div
        className="absolute top-40 left-8 w-6 h-6 bg-orange-400/30 rounded-full animate-float blur-sm"
        style={{ animationDelay: "1s" }}
      ></div>
      <div
        className="absolute bottom-32 right-16 w-3 h-3 bg-amber-400/50 rounded-full animate-float blur-sm"
        style={{ animationDelay: "2s" }}
      ></div>
      <div
        className="absolute top-60 right-20 w-5 h-5 bg-yellow-300/35 rounded-full animate-float blur-sm"
        style={{ animationDelay: "3s" }}
      ></div>

      <div className="relative max-w-4xl mx-auto p-4 safe-area-x animate-fade-in">
        <NotificationBanner />

        <StreakDisplay />

        <div className="glass-effect rounded-2xl p-4 md:p-6 mb-4 md:mb-6 animate-scale-in shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-1">
                  Today's Plan
                </h1>
                <p className="text-gray-600">
                  Woke up at {formatTime(wakeTime)}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleClearPlan}
                className="px-4 py-2 bg-white text-red-600 rounded-lg font-medium hover:bg-red-50 hover:shadow-md active:scale-95 border border-red-200 transition-all duration-200"
              >
                Clear Plan
              </button>
              <button
                onClick={handleWakeUp}
                disabled={waking}
                className="px-4 py-2 bg-white text-gray-700 rounded-lg font-medium hover:bg-gray-50 hover:shadow-md active:scale-95 border border-gray-200 disabled:opacity-50 transition-all duration-200"
              >
                Recompute
              </button>
            </div>
          </div>

          {plan.recompute_count > 0 && (
            <div className="text-sm text-blue-700 bg-blue-100 rounded-lg px-3 py-2">
              Recomputed {plan.recompute_count} time
              {plan.recompute_count !== 1 ? "s" : ""} today
            </div>
          )}
        </div>

        <FunFact />

        <div className="space-y-3">
          {groupItems(plan.items).map((groupedItem, index) => {
            if (groupedItem.type === "group" && groupedItem.groupInstanceId) {
              return (
                <div
                  key={groupedItem.groupInstanceId}
                  className="animate-slide-in"
                  style={{ animationDelay: `${index * 40}ms` }}
                >
                  <GroupCard
                    groupInstanceId={groupedItem.groupInstanceId}
                    items={groupedItem.items}
                    onTaken={handleGroupTaken}
                    onMissed={handleGroupMissed}
                    onSnooze={handleGroupSnooze}
                    showParticles={groupCompletionStates.has(
                      groupedItem.groupInstanceId
                    )}
                  />
                </div>
              );
            }

            const item = groupedItem.items[0];
            const plannedTime = new Date(item.planned_time);
            const isPast = now > plannedTime;
            const isExpanded = expandedItems.has(item.id);
            const hasConflicts = item.conflict_notes.length > 0;

            return (
              <div
                key={item.id}
                className="animate-slide-in"
                style={{ animationDelay: `${index * 40}ms` }}
              >
                <div
                  className={`rounded-xl border-2 p-4 hover:scale-[1.01] transition-all duration-300 relative ${getStatusColor(
                    item.status
                  )}`}
                >
                  {completionStates.some(
                    (s) => s.itemId === item.id && s.showParticles
                  ) && <ParticleBurst />}
                  <div className="flex items-start gap-3">
                    {getStatusIcon(item.status)}

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">
                            {item.preset_snapshot.name}
                          </h3>
                          <div className="flex items-center gap-2 text-sm text-gray-600 mt-1">
                            <Clock size={14} />
                            <span className="font-medium">
                              {formatTime(plannedTime)}
                            </span>
                            {item.is_next_day && (
                              <span className="bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium">
                                +1 day
                              </span>
                            )}
                            {isPast && item.status === "PLANNED" && (
                              <span className="text-red-600 font-medium">
                                Overdue
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-3">
                        <span className="text-xs bg-white bg-opacity-60 px-2 py-1 rounded">
                          {item.preset_snapshot.tag}
                        </span>
                        <span className="text-xs bg-white bg-opacity-60 px-2 py-1 rounded">
                          {formatFoodRelation(
                            item.preset_snapshot.food_relation
                          )}
                        </span>
                        {item.shift_minutes > 0 && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded font-medium">
                            +{item.shift_minutes}m shifted
                          </span>
                        )}
                      </div>

                      {(item.status === "PLANNED" ||
                        item.status === "SNOOZED") && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => handleTaken(item.id)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 hover:shadow-lg active:scale-95 transition-all duration-200"
                          >
                            Taken
                          </button>
                          <button
                            onClick={() => handleSnooze(item.id, 10)}
                            className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 hover:shadow-lg active:scale-95 transition-all duration-200"
                          >
                            +10m
                          </button>
                          <button
                            onClick={() => handleSnooze(item.id, 30)}
                            className="px-4 py-2 bg-yellow-500 text-white rounded-lg text-sm font-medium hover:bg-yellow-600 hover:shadow-lg active:scale-95 transition-all duration-200"
                          >
                            +30m
                          </button>
                          <button
                            onClick={() => handleMissed(item.id)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 hover:shadow-lg active:scale-95 transition-all duration-200"
                          >
                            Missed
                          </button>
                        </div>
                      )}

                      {item.status === "TAKEN" && item.actual_time && (
                        <p className="text-sm text-green-700">
                          Taken at {formatTime(new Date(item.actual_time))}
                        </p>
                      )}

                      {hasConflicts && (
                        <button
                          onClick={() => toggleExpanded(item.id)}
                          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mt-3"
                        >
                          {isExpanded ? (
                            <ChevronUp size={16} />
                          ) : (
                            <ChevronDown size={16} />
                          )}
                          {isExpanded ? "Hide" : "Show"} conflict details
                        </button>
                      )}

                      {isExpanded && hasConflicts && (
                        <div className="mt-3 p-3 bg-white bg-opacity-60 rounded-lg">
                          <p className="text-xs font-medium text-gray-700 mb-2">
                            Scheduling adjustments:
                          </p>
                          <ul className="space-y-1">
                            {item.conflict_notes.map((note, i) => (
                              <li
                                key={i}
                                className="text-xs text-gray-600 flex items-start gap-2"
                              >
                                <span className="text-orange-500 mt-0.5">
                                  •
                                </span>
                                <span>{note}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {toast && (
          <Toast
            message={toast.message}
            onClose={() => setToast(null)}
            onUndo={toast.onUndo}
          />
        )}

        {unlockedBadge && (
          <BadgeUnlockModal
            badge={unlockedBadge}
            onClose={() => setUnlockedBadge(null)}
          />
        )}

        <PlanGenerationModal
          isVisible={showGenerationModal}
          isRecompute={isRecompute}
        />
      </div>
    </div>
  );
}
